import { DataAccess } from "../dataaccess/data.access";
import { Cache, Invalidate } from "../decorators/cache.decorator";
import { Timeout } from "../decorators/timeout.decorator";
import { S25Util } from "../util/s25-util";
import { FlsService } from "./fls.service";
import { PreferenceService } from "./preference.service";
import { UserprefService } from "./userpref.service";
import { GroupPreferenceService } from "./group.preference.service";

//@author: Travis
export class WelcomeMessageService {
    @Timeout
    @Cache({ immutable: true, targetName: "WelcomeMessageService" })
    public static async getWelcomeMessage(): Promise<string> {
        const welcomeMessage = await WelcomeMessageService.getWelcomeMessages();
        return welcomeMessage?.group_welcome_message || welcomeMessage?.message || "";
    }

    public static async getGlobalWelcomeMessage(): Promise<string> {
        const welcomeMessage = await WelcomeMessageService.getWelcomeMessages();
        return welcomeMessage?.message || "";
    }

    public static async getWelcomeMessages(): Promise<WelcomeMessageResponse> {
        const [welcomeMessage, groupPreferences] = await Promise.all([
            DataAccess.get<{ welcome_message: WelcomeMessageResponse }>(
                DataAccess.injectCaller("/welcome/message.json", "WelcomeMessageService.getWelcomeMessages"),
            ),
            GroupPreferenceService.getGroupPreferences(["welcome_message"]),
        ]);

        return {
            message: welcomeMessage?.welcome_message?.message,
            group_welcome_message: groupPreferences?.find((p) => p.prefName === "welcome_message")?.prefValue || "",
        };
    }

    @Timeout
    @Invalidate({ methodName: "WelcomeMessageService.getWelcomeMessage" })
    public static setWelcomeMessage(message: string) {
        return DataAccess.post(
            DataAccess.injectCaller("/welcome/message.json", "WelcomeMessageService.setWelcomeMessage"),
            { welcome_message: { message: message } },
        );
    }

    @Timeout
    public static hasWelcomeMessageWritePerms() {
        return FlsService.getFls().then(function (fls) {
            return fls && fls.SYS_MOTD === "F";
        });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "WelcomeMessageService" })
    public static hasAutoShowWelcomeMessage() {
        return S25Util.all({
            isLoggedIn: UserprefService.getLoggedIn(),
            prefs: PreferenceService.getGenericPreference("welcome", ["autoShow"]),
        }).then(function (data) {
            return !data.isLoggedIn || !data.prefs || S25Util.coalesce(data.prefs.autoShow, "T") === "T";
        });
    }

    @Timeout
    @Invalidate({ methodName: "WelcomeMessageService.hasAutoShowWelcomeMessage" })
    public static setAutoShowWelcomeMessage(value: any) {
        return PreferenceService.setGenericPreference("welcome", "autoShow", value);
    }
}

export interface WelcomeMessageResponse {
    message: string;
    group_welcome_message?: string;
}
